import type Graph from 'graphology';
import { X } from 'lucide-react';
import type Mermaid from 'mermaid';
import { useEffect, useRef, useState } from 'react';

import { generateProcessMermaid } from '../lib/mermaid-generator.js';

let mermaidInitialized = false;

type MermaidApi = typeof Mermaid;

async function ensureMermaid(): Promise<MermaidApi> {
  const mod = await import('mermaid');
  if (!mermaidInitialized) {
    mod.default.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'strict' });
    mermaidInitialized = true;
  }
  return mod.default;
}

export interface ProcessFlowModalProps {
  readonly graph: Graph | null;
  readonly processNodeId: string | null;
  readonly onClose: () => void;
}

export function ProcessFlowModal({
  graph,
  processNodeId,
  onClose,
}: ProcessFlowModalProps): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!processNodeId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [processNodeId, onClose]);

  useEffect(() => {
    if (!processNodeId || !graph) return;
    let cancelled = false;
    const renderId = `mermaid-${Math.random().toString(36).slice(2)}`;
    const source = generateProcessMermaid(graph, processNodeId);

    setError(null);
    void ensureMermaid()
      .then((m) => m.render(renderId, source))
      .then(({ svg }) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = svg;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to render diagram.');
      });

    return (): void => {
      cancelled = true;
    };
  }, [graph, processNodeId]);

  if (!processNodeId) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Process flow diagram"
      onClick={onClose}
      className="fixed inset-0 z-50 grid animate-fade-in place-items-center bg-void/70 p-4 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-panel flex max-h-[86vh] w-[min(92vw,1100px)] animate-slide-up flex-col overflow-hidden rounded-2xl border border-border-subtle text-text-primary shadow-panel"
      >
        <header className="flex items-center justify-between border-b border-dashed border-border-subtle px-5 py-3">
          <div className="flex items-center gap-2">
            <span
              className="grid h-6 w-6 place-items-center rounded-md bg-gradient-to-br from-cyan-400 to-sky-500 text-[10px] font-bold text-white"
              aria-hidden
            >
              ▷
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
              Process Flow
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-7 w-7 place-items-center rounded-md text-text-muted transition-colors hover:bg-hover hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="scrollbar-thin flex-1 overflow-auto bg-deep p-6">
          {error ? (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
              Error: {error}
            </div>
          ) : (
            <div ref={containerRef} className="w-full overflow-auto" />
          )}
        </div>
      </div>
    </div>
  );
}
